import React, { useEffect } from 'react';
import {
  BaseButton,
  ButtonGroup,
  Drawer,
  ErrorHandler,
} from '@common/components';
import {
  NewSupplierFormValues,
  SuppliersManagementNewSupplierForm,
} from '@institutions/components';
import { Form } from 'antd';
import { useMutation, useQuery } from '@common/hooks';
import {
  REINVITE_SUPPLIER_TO_SIGNUP_MUTATION,
  REINVITE_SUPPLIER_TO_SIGNUP_QUERY,
} from '@institutions/graphql';
import { createMessage } from 'works/legacy/lib/message-helper';

export function SuppliersManagementReinviteDrawer({
  open,
  onClose,
  supplierId,
}: {
  open: boolean;
  onClose: () => void;
  supplierId?: string;
}) {
  const [form] = Form.useForm();

  const {
    data,
    loading: loadingSupplierData,
    error,
  } = useQuery(REINVITE_SUPPLIER_TO_SIGNUP_QUERY, {
    variables: { id: supplierId || '' },
    skip: !supplierId,
  });

  useEffect(() => {
    if (data?.supplier?.supplierSignupInvitation) {
      form.setFieldsValue({
        supplierName: data.supplier.name,
        mainContactEmail: data.supplier.supplierSignupInvitation.email,
      });
    }
  }, [data, form]);

  const showErrorMessage = () =>
    createMessage('error', 'Failed to reinvite supplier. Please try again.');

  const [reinviteSupplierToSignup, { loading: loadingMutation }] = useMutation(
    REINVITE_SUPPLIER_TO_SIGNUP_MUTATION,
    {
      onCompleted: ({ reinviteSupplierToSignup }) => {
        if (reinviteSupplierToSignup?.success) {
          createMessage('success', 'Supplier reinvited successfully');
          onClose();
          form.resetFields();
        } else {
          showErrorMessage();
        }
      },
      onError: showErrorMessage,
      refetchQueries: ['Supplier', 'SupplierHistory'],
    }
  );

  if (error) {
    return (
      <ErrorHandler
        error={error}
        log="SuppliersManagementReinviteDrawer"
        messageType="contextual"
      />
    );
  }

  const invitationId = data?.supplier?.supplierSignupInvitation?.id || '';

  const onFormFinish = (values: NewSupplierFormValues) => {
    if (supplierId) {
      reinviteSupplierToSignup({
        variables: {
          invitationId,
          input: {
            supplierName: values.supplierName,
            mainContactEmail: values.mainContactEmail,
            invitationMessage: values.invitationMessage,
          },
        },
      });
    }
  };

  return (
    <Drawer
      title="Reinvite"
      open={open}
      onClose={onClose}
      footer={
        <ButtonGroup>
          <BaseButton size="large" onClick={onClose} disabled={loadingMutation}>
            Cancel
          </BaseButton>
          <BaseButton
            size="large"
            type="primary"
            onClick={form.submit}
            loading={loadingMutation}
            disabled={!supplierId}
          >
            Reinvite
          </BaseButton>
        </ButtonGroup>
      }
    >
      <SuppliersManagementNewSupplierForm
        form={form}
        onFormFinish={onFormFinish}
        loading={loadingSupplierData}
      />
    </Drawer>
  );
}
